import { AlertCircle } from 'lucide-react';
import { useState } from 'react';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Modal } from '@/components/Modal';
import { Table, TableColumn } from '@/components/Table';

import { useBulkApproveClaims } from '@/hooks/useBulkApproveClaims';

interface SelectedClaim {
  claimId: string;
  userEmail: string;
}

interface BulkApproveModalProps {
  selectedClaims: SelectedClaim[];
  targetStatus: string;
  onClose: () => void;
  onFullSuccess: () => void;
  onPartialOrTotalFailure: (retainedClaimIds: string[]) => void;
}

type Phase = 'confirm' | 'submitting' | 'result';

interface FailureRow extends SelectedClaim {
  id?: string;
  error?: string;
}

const failureColumns: TableColumn<FailureRow>[] = [
  { header: 'User Email', accessor: (r) => r.userEmail },
  { header: 'Error Reason', accessor: (r) => r.error ?? 'Unknown error' },
];

/** Copy per bulk target status. The `approved` entry keeps the original wording unchanged. */
const TARGET_STATUS_COPY: Record<
  string,
  { title: string; verb: string; gerund: string; pastVerb: string }
> = {
  approved: { title: 'Approve Claims', verb: 'approve', gerund: 'Approving', pastVerb: 'approved' },
  paid: {
    title: 'Mark Claims as Paid',
    verb: 'mark as paid',
    gerund: 'Marking as paid',
    pastVerb: 'marked as paid',
  },
};

export function BulkApproveModal({
  selectedClaims,
  targetStatus,
  onClose,
  onFullSuccess,
  onPartialOrTotalFailure,
}: BulkApproveModalProps) {
  const copy = TARGET_STATUS_COPY[targetStatus] ?? {
    title: 'Change Claim Status',
    verb: 'update',
    gerund: 'Updating',
    pastVerb: 'updated',
  };
  const { bulkApprove, error, reset } = useBulkApproveClaims();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [failureRows, setFailureRows] = useState<FailureRow[]>([]);
  const [summary, setSummary] = useState<{ kind: 'partial' | 'total-failure'; text: string }>({
    kind: 'partial',
    text: '',
  });

  const isSubmitting = phase === 'submitting';

  const submit = async () => {
    setPhase('submitting');
    try {
      const response = await bulkApprove({ claims: selectedClaims, newStatus: targetStatus });
      const total = response.results.length;
      const successCount = response.results.filter((r) => r.success).length;
      const failCount = total - successCount;

      if (failCount === 0) {
        onFullSuccess();
        return;
      }

      const failed = response.results.filter((r) => !r.success);
      const rows = failed.map((r) => {
        const claim = selectedClaims.find((c) => c.claimId === r.claimId);
        return { claimId: r.claimId, userEmail: claim?.userEmail ?? '', error: r.error };
      });
      setFailureRows(rows);
      setSummary({
        kind: 'partial',
        text: `${successCount} of ${total} claims ${copy.pastVerb}. ${failCount} failed.`,
      });
      setPhase('result');
      onPartialOrTotalFailure(failed.map((r) => r.claimId));
    } catch {
      setSummary({ kind: 'total-failure', text: 'No claims were modified.' });
      setPhase('result');
      onPartialOrTotalFailure(selectedClaims.map((c) => c.claimId));
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleRetry = () => {
    reset();
    setFailureRows([]);
    void submit();
  };

  return (
    <Modal
      title={copy.title}
      onClose={handleClose}
      maxWidth="lg"
      headerSticky
      closeButtonDisabled={isSubmitting}
      closeOnEscape={!isSubmitting}
      closeOnBackdropClick={!isSubmitting}
      titleSize="lg"
    >
      {phase === 'confirm' && (
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <div className="flex size-12 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="size-6 text-red-600" />
              </div>
            </div>
            <p className="flex-1 text-gray-600">
              Are you sure you want to {copy.verb}{' '}
              <span className="font-semibold">{selectedClaims.length}</span> claim
              {selectedClaims.length === 1 ? '' : 's'}?
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              className="flex-1 rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700"
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {phase === 'submitting' && (
        <LoadingSpinner message={`${copy.gerund} ${selectedClaims.length} claims...`} />
      )}

      {phase === 'result' && summary.kind === 'partial' && (
        <div className="space-y-4">
          <p className="text-gray-800">{summary.text}</p>
          <Table<FailureRow>
            columns={failureColumns}
            data={failureRows}
            getRowKey={(r) => r.claimId}
            emptyMessage={<span className="text-gray-500">No failures</span>}
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {phase === 'result' && summary.kind === 'total-failure' && (
        <div className="space-y-4">
          <p className="font-medium text-gray-800">{summary.text}</p>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleRetry}
              className="rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
